// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { useOrganization } from '@/auth/clerkShim';
import _ from 'lodash';
import { format } from 'date-fns';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Trash2, 
  Eye, 
  Calendar,
  User,
  Globe,
  Activity,
  ArrowUpDown,
  ChevronDown
} from 'lucide-react';

// UI Components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import adminStore from '@/store/adminStore';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { TableSkeleton } from '@/components/Page/PageSkeletons';

// Store
import useAuditLogStore from '@/store/auditLogStore';

// Types
interface AuditLog {
  _id: string;
  userId: string;
  email?: string;
  displayEmail?: string;
  sessionId?: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  originalUrl: string;
  ip?: string;
  userAgent?: string;
  requestBody?: any;
  requestParams?: any;
  requestQuery?: any;
  responseStatus?: number;
  responseBody?: any;
  leagueId?: string;
  organizationId?: string;
  metadata?: any;
  createdAt: string;
}

// Radix Select throws on empty-string item values, so "no filter" options use
// this sentinel and map back to '' before hitting the store/query string.
const ALL = 'all';

const DEFAULT_FILTERS = {
  userId: '',
  method: '',
  url: '',
  status: '',
  leagueId: '',
  startDate: '',
  endDate: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

export default function LogsAdmin() {
  const { getToken } = useAuth();
  const { organization, isLoaded } = useOrganization();
  
  // Store
  const {
    auditLogs,
    loading,
    pagination,
    filters,
    stats,
    getAuditLogs,
    getAuditLogStats,
    setFilters,
    resetFilters,
    setPagination,
    cleanupOldLogs
  } = useAuditLogStore();

  // Local state
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [memberships, setMemberships] = useState([]);

  const getMembershipsFactory = adminStore((state: any) => state.getMemberships);
  const getMemberships = getMembershipsFactory(getToken);

  // Audit logs are scoped by organizationId (below). The legacy per-league
  // sub-filter came from Clerk org privateMetadata, which no longer exists under
  // the Mongo auth shim — leave it unset (org scope is the source of truth).
  const leagueId = undefined;

  // Load data when filters change
  useEffect(() => {
    if (getToken && organization) {
      loadData();
    }
  }, [filters, pagination.page]);

  useEffect(() => {
    if (!organization) return;
    loadData();
    getMemberships(organization.id, (res) => {
      setMemberships(res);
    });
  }, [isLoaded, organization?.id]);


  console.log('LogsAdmin::memberships', memberships);

  // Create email and role mapping from memberships
  const createUserMappings = () => {
    const emailMap = {};
    const roleMap = {};
    memberships.forEach(membership => {
      // Try multiple possible locations for userId and email based on the actual data structure
      const userId = membership.publicMetadata?.userId || membership.publicUserData?.userId;
      const email = membership.publicMetadata?.email || membership.publicUserData?.identifier;
      const role = membership.role;
      
      if (userId && email) {
        emailMap[userId] = email;
        roleMap[userId] = role;
      }
    });
    console.log('LogsAdmin::emailMap', emailMap);
    console.log('LogsAdmin::roleMap', roleMap);
    return { emailMap, roleMap };
  };

  const loadData = async () => {
    if (!getToken || !organization) return;

    const token = await getToken();
    const getAuditLogsWithToken = getAuditLogs(getToken);
    const getAuditLogStatsWithToken = getAuditLogStats(getToken);

    // Load audit logs with organization filter (organizationId is now required)
    await getAuditLogsWithToken({ 
      leagueId,
      organizationId: organization.id 
    });
    
    // Load statistics with organization filter
    await getAuditLogStatsWithToken({ 
      leagueId,
      organizationId: organization.id 
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(localFilters);
  };

  const handleResetFilters = () => {
    resetFilters();
    setLocalFilters(DEFAULT_FILTERS);
  };

  const handlePageChange = (page: number) => {
    setPagination({ page });
  };

  const handleCleanup = async () => {
    if (!getToken) return;
    
    const cleanupWithToken = cleanupOldLogs(getToken);
    await cleanupWithToken(90); // Clean up logs older than 90 days
    await loadData(); // Reload data after cleanup
  };

  const getMethodBadgeColor = (method: string) => {
    switch (method) {
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-blue-100 text-blue-800';
      case 'PATCH': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-muted text-foreground';
    }
  };

  const getStatusBadgeColor = (status?: number) => {
    if (!status) return 'bg-muted text-foreground';
    if (status >= 200 && status < 300) return 'bg-success/10 text-success';
    if (status >= 400 && status < 500) return 'bg-warning/10 text-warning';
    if (status >= 500) return 'bg-destructive/10 text-destructive';
    return 'bg-muted text-foreground';
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Audit Logs"
        description="Monitor all user activities and system changes"
        actions={(
          <>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button
              variant="outline"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleCleanup}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Cleanup
            </Button>
          </>
        )}
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logs</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLogs}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.topUsers.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Endpoints</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.topEndpoints.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Activity</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.dailyActivity[0]?.count || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>Filter audit logs by various criteria</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">User ID</label>
                <Input
                  placeholder="Filter by user ID"
                  value={localFilters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">HTTP Method</label>
                <Select
                  value={localFilters.method || ALL}
                  onValueChange={(value) => handleFilterChange('method', value === ALL ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All methods</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="PATCH">PATCH</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={localFilters.status || ALL}
                  onValueChange={(value) => handleFilterChange('status', value === ALL ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    <SelectItem value="errors">Errors (4xx + 5xx)</SelectItem>
                    <SelectItem value="4xx">4xx Client errors</SelectItem>
                    <SelectItem value="5xx">5xx Server errors</SelectItem>
                    <SelectItem value="2xx">2xx Success</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">URL</label>
                <Input
                  placeholder="Filter by URL"
                  value={localFilters.url}
                  onChange={(e) => handleFilterChange('url', e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={localFilters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input
                  type="date"
                  value={localFilters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Sort By</label>
                <Select
                  value={localFilters.sortBy}
                  onValueChange={(value) => handleFilterChange('sortBy', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="createdAt">Date</SelectItem>
                    <SelectItem value="method">Method</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="userId">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters}>Apply Filters</Button>
              <Button variant="outline" onClick={handleResetFilters}>
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>
            Showing {auditLogs.length} of {pagination.total} logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <TableSkeleton columns={7} />
          ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User (Role)</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <p className="text-muted-foreground">No audit logs found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log: AuditLog) => (
                    <TableRow key={log._id}>
                      <TableCell className="font-mono text-sm">
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {(() => {
                          const { emailMap, roleMap } = createUserMappings();
                          const email = log.displayEmail || log.email || emailMap[log.userId] || truncateText(log.userId, 20);
                          const role = roleMap[log.userId];
                          return role ? `${email} (${role})` : email;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge className={getMethodBadgeColor(log.method)}>
                          {log.method}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={log.url}>
                          {truncateText(log.url, 40)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.responseStatus && (
                          <Badge className={getStatusBadgeColor(log.responseStatus)}>
                            {log.responseStatus}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.ip || '-'}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="View log details"
                              onClick={() => setSelectedLog(log)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Audit Log Details</DialogTitle>
                              <DialogDescription>
                                Detailed information about this audit log entry
                              </DialogDescription>
                            </DialogHeader>
                            {selectedLog && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-semibold">Basic Information</h4>
                                    <div className="space-y-2 text-sm">
                                      <div><strong>ID:</strong> {selectedLog._id}</div>
                                      <div><strong>User ID:</strong> {selectedLog.userId}</div>
                                      <div><strong>Email:</strong> {(() => {
                                        const { emailMap, roleMap } = createUserMappings();
                                        const email = selectedLog.displayEmail || selectedLog.email || emailMap[selectedLog.userId] || '-';
                                        const role = roleMap[selectedLog.userId];
                                        return role ? `${email} (${role})` : email;
                                      })()}</div>
                                      <div><strong>Session ID:</strong> {selectedLog.sessionId || '-'}</div>
                                      <div><strong>Method:</strong> {selectedLog.method}</div>
                                      <div><strong>URL:</strong> {selectedLog.url}</div>
                                      <div><strong>IP Address:</strong> {selectedLog.ip || '-'}</div>
                                      <div><strong>Status:</strong> {selectedLog.responseStatus || '-'}</div>
                                      <div><strong>Created:</strong> {formatDate(selectedLog.createdAt)}</div>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold">Metadata</h4>
                                    <div className="space-y-2 text-sm">
                                      <div><strong>League ID:</strong> {selectedLog.leagueId || '-'}</div>
                                      <div><strong>Organization ID:</strong> {selectedLog.organizationId || '-'}</div>
                                      <div><strong>Auth Type:</strong> {selectedLog.metadata?.authType || '-'}</div>
                                      <div><strong>Page:</strong> {selectedLog.metadata?.page || '-'}</div>
                                      <div><strong>User Agent:</strong> {truncateText(selectedLog.userAgent || '', 50)}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold">Request Body</h4>
                                  <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-32">
                                    {JSON.stringify(selectedLog.requestBody, null, 2) || 'No request body'}
                                  </pre>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold">Response Body</h4>
                                  <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-32">
                                    {JSON.stringify(selectedLog.responseBody, null, 2) || 'No response body'}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(pagination.page - 1)}
                      className={pagination.page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {(() => {
                    const totalPages = pagination.pages;
                    const currentPage = pagination.page;
                    const maxVisible = 5;
                    
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    
                    // Adjust start page if we're near the end
                    if (endPage - startPage + 1 < maxVisible) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
                    
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(i);
                    }
                    
                    return pages.map(page => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => handlePageChange(page)}
                          isActive={page === currentPage}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ));
                  })()}
                  
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(pagination.page + 1)}
                      className={pagination.page >= pagination.pages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}